import { Clubs } from 'app/design/icons/clubs';
import { Box, Column, Row } from 'app/design/primitives/box';
import { Switch } from 'app/design/primitives/switch';
import { Text } from 'app/design/primitives/text';

import { useFormContext } from 'app/hooks/form';
import { useAllAddresses } from 'app/network/address';
import { useListingVisibilityGroups } from 'app/network/groups';
import type { CreateListingDTO } from 'app/types/listing';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import React, { type Dispatch, type SetStateAction } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ListingsVisibilityExclusionsPreference } from '../index';
import { getAddressIcon } from 'app/screens/profile/my-clubs';
import { PCImage } from 'app/design/primitives/image';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconInfoCircle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

function VisibilityRow({ children }: { children: React.ReactNode }) {
  return (
    <Row
      alignItems="center"
      backgroundColor="surface.default"
      borderRadius="radius.sm"
      justifyContent="space-between"
      paddingHorizontal="spacing.general.x2"
      paddingVertical="spacing.general.x2"
    >
      {children}
    </Row>
  );
}

function VisibilityIcon({ children }: { children: React.ReactNode }) {
  return (
    <Column alignItems="center" borderRadius="radius.xl" height={40} justifyContent="center" width={40}>
      {children}
    </Column>
  );
}

export function GroupsSection({
  alwaysShow = false,
  includeAllSectorGroups = false,
  setListingsVisibilityExclusionsPreference,
}: {
  alwaysShow?: boolean;
  includeAllSectorGroups?: boolean;
  setListingsVisibilityExclusionsPreference?: Dispatch<SetStateAction<ListingsVisibilityExclusionsPreference>>;
}) {
  const { data: allGroups } = useListingVisibilityGroups(includeAllSectorGroups);
  const { t } = useTranslation('new');
  const { data: allAddresses } = useAllAddresses();
  const { control, watch } = useFormContext<CreateListingDTO>();

  const watchedAddressIds = watch('addressIds');
  const watchedGroupIds = watch('groupIds');
  const justOneClubEnabled = isFeatureEnabled(FeatureFlag.justOneClub);

  const persistVisibilityPreference = (addressIds?: string[], groupIds?: string[]) => {
    if (!allAddresses) return;
    if (!allGroups) return;
    if (!setListingsVisibilityExclusionsPreference) return;

    const selectedAddressIds = new Set(addressIds);
    const selectedGroupIds = new Set(groupIds);
    const excludedAddressIds: string[] = [];
    const excludedGroupIds: string[] = [];
    for (const address of allAddresses) {
      if (!selectedAddressIds.has(address.id)) excludedAddressIds.push(address.id);
    }
    for (const group of allGroups) {
      if (!selectedGroupIds.has(group.id)) excludedGroupIds.push(group.id);
    }

    setListingsVisibilityExclusionsPreference({
      addressIds: excludedAddressIds,
      groupIds: excludedGroupIds,
    });
  };

  if (!allGroups?.length && (allAddresses?.length ?? 0) <= 1 && !alwaysShow) return null;

  if (justOneClubEnabled && !alwaysShow) return null;

  const selectedAddressCount = watchedAddressIds ? watchedAddressIds.length : 0;
  const selectedGroupCount = watchedGroupIds ? watchedGroupIds.length : 0;
  const hasNoSelectedVisibility = selectedAddressCount + selectedGroupCount === 0;
  const showInvisibleWarning = hasNoSelectedVisibility ? !justOneClubEnabled : false;

  return (
    <Box
      backgroundColor="surface.secondary-neutral"
      borderRadius="radius.lg"
      gap="spacing.general.x4"
      paddingHorizontal="spacing.general.x5"
      paddingVertical="spacing.general.x6"
    >
      <Text fontWeight="700" variant="bodyLarge" textAlign="center">
        {t('locationSection.title')}
      </Text>

      {!justOneClubEnabled && allAddresses ? (
        <Column
          borderColor="border.secondary"
          borderRadius="radius.sm"
          borderWidth={1}
          gap="spacing.general.x2"
          paddingHorizontal="spacing.general.x4"
          paddingVertical="spacing.general.x4"
        >
          <Text fontWeight="700" variant="bodySmall">
            {t('locationSection.addressesTitle')}
          </Text>
          <Controller
            {...{ control }}
            name="addressIds"
            render={({ field }) => (
              <Column gap="spacing.general.x2">
                {allAddresses.map((address) => {
                  const isHomeAddress = !address.name || address.name === 'Domicile' || address.name === 'Home';

                  return (
                    <VisibilityRow key={address.id}>
                      <Row
                        alignItems="center"
                        flex={1}
                        gap="spacing.general.x2"
                        marginEnd="spacing.general.x2"
                        opacity={Boolean(field.value?.includes(address.id)) ? 1 : 0.6}
                      >
                        <VisibilityIcon>{getAddressIcon(address, 36)}</VisibilityIcon>
                        <Column flex={1}>
                          <Text flex={1} fontWeight="700" variant="bodyBase">
                            {isHomeAddress
                              ? t('locationSection.addressFieldTitle')
                              : t('locationSection.groupFieldTitle', {
                                  clubName: address.name,
                                })}
                          </Text>
                          <Text
                            color="text.secondary"
                            flex={1}
                            marginTop="spacing.general.x0,5"
                            variant="bodyBase"
                            fontSize={12}
                          >
                            {isHomeAddress
                              ? t('locationSection.addressFieldSubtitle')
                              : t('locationSection.addressFieldSubtitleCustom', {
                                  clubName: address.name,
                                })}
                          </Text>
                        </Column>
                      </Row>

                      <Switch
                        onValueChange={(on) => {
                          const newValue = on
                            ? [...(field.value ?? []), address.id]
                            : field.value?.filter((addressId) => addressId !== address.id);
                          field.onChange(newValue);
                          persistVisibilityPreference(newValue, watchedGroupIds);
                        }}
                        value={Boolean(field.value?.includes(address.id))}
                        alignSelf="center"
                      />
                    </VisibilityRow>
                  );
                })}
              </Column>
            )}
          />
        </Column>
      ) : null}

      {allGroups && allGroups.length > 0 ? (
        <Column
          borderColor="border.secondary"
          borderRadius="radius.sm"
          borderWidth={1}
          gap="spacing.general.x2"
          paddingHorizontal="spacing.general.x4"
          paddingVertical="spacing.general.x4"
        >
          <Text fontWeight="700" variant="bodySmall">
            {t('locationSection.clubsTitle')}
          </Text>

          <Controller
            {...{ control }}
            name="groupIds"
            render={({ field }) => (
              <Column gap="spacing.general.x2">
                {allGroups.map((group) => (
                  <VisibilityRow key={group.id}>
                    <Row
                      alignItems="center"
                      flex={1}
                      gap="spacing.general.x2"
                      marginEnd="spacing.general.x2"
                      opacity={Boolean(field.value?.includes(group.id)) ? 1 : 0.6}
                    >
                      <VisibilityIcon>
                        {group.picture ? (
                          <PCImage
                            source={{ uri: group.picture }}
                            height={36}
                            width={36}
                            style={{ borderRadius: theme.borderRadii['radius.lg'] }}
                          />
                        ) : (
                          <Clubs color={theme.colors['text.primary']} height={36} width={36} />
                        )}
                      </VisibilityIcon>
                      <Column flex={1}>
                        {!justOneClubEnabled ? (
                          <Text fontWeight="700" variant="bodyBase">
                            {t('locationSection.groupFieldTitle', {
                              clubName: group.name,
                            })}
                          </Text>
                        ) : null}
                        {justOneClubEnabled ? (
                          <Text color="text.secondary" variant="bodyBase" fontSize={12}>
                            {t('locationSection.oneGroupFieldHelp')}
                          </Text>
                        ) : null}
                      </Column>
                    </Row>

                    <Switch
                      onValueChange={(on) => {
                        const newValue = on
                          ? [...(field.value ?? []), group.id]
                          : field.value?.filter((groupId) => groupId !== group.id);
                        field.onChange(newValue);
                        persistVisibilityPreference(watchedAddressIds, newValue);
                      }}
                      value={Boolean(field.value?.includes(group.id))}
                    />
                  </VisibilityRow>
                ))}
              </Column>
            )}
          />
        </Column>
      ) : null}

      {showInvisibleWarning ? (
        <Row alignItems="center" gap="spacing.general.x1" marginTop="spacing.general.x2">
          <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} size={16} color={theme.colors['text.secondary']} />
          <Text color="text.secondary" flex={1} variant="bodyBase">
            {t('locationSection.invisible')}
          </Text>
        </Row>
      ) : null}
    </Box>
  );
}
